import { useEffect, useState } from 'react';
import { Activity, Clock, Loader2, CheckCircle, XCircle, RefreshCcw } from 'lucide-react';

// تعريف الأنواع محلياً للتوضيح (يُفضل جلبها من packages/types)
interface AiJob {
  uuid: string;
  target_type: string;
  task_type: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  created_at: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

export default function AiJobs() {
  const [jobs, setJobs] = useState<AiJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('jwt_token') || '';
      const response = await fetch(`${API_URL}/ai/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(Array.isArray(data) ? data : (data.jobs || []));
      }
    } catch (error) {
      console.error('Failed to fetch AI jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // تحديث تلقائي كل 10 ثوانٍ لمراقبة المهام
    const interval = setInterval(fetchJobs, 10000);
    return () => clearInterval(interval);
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'queued':
        return <span className="flex items-center gap-1 text-slate-500 bg-slate-100 px-2 py-1 rounded text-xs font-medium"><Clock className="w-3 h-3" /> في الانتظار</span>;
      case 'processing':
        return <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded text-xs font-medium"><Loader2 className="w-3 h-3 animate-spin" /> جاري المعالجة</span>;
      case 'completed':
        return <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-1 rounded text-xs font-medium"><CheckCircle className="w-3 h-3" /> مكتملة</span>;
      case 'failed':
        return <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded text-xs font-medium"><XCircle className="w-3 h-3" /> فشلت</span>;
      default:
        return <span className="text-slate-500">{status}</span>;
    }
  };

  const getTaskTypeName = (type: string) => {
    const types: Record<string, string> = {
      'image_synthesis': 'توليد صور المشاهد',
      'audio_alignment': 'مزامنة النص والصوت',
      'audio_separation': 'عزل وتنقية الصوت'
    };
    return types[type] || type;
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-slate-700" />
            تتبع مهام الذكاء الاصطناعي
          </h1>
          <p className="text-sm text-slate-500 mt-1">مراقبة حية لعمليات المعالجة في الخلفية (Cloudflare Queues)</p>
        </div>
        
        <button 
          onClick={fetchJobs}
          className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
          title="تحديث البيانات"
        >
          <RefreshCcw className={`w-5 h-5 ${isLoading ? 'animate-spin text-slate-400' : ''}`} />
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-right">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="p-4 text-sm font-semibold text-slate-600">رقم المهمة (UUID)</th>
              <th className="p-4 text-sm font-semibold text-slate-600">نوع العملية</th>
              <th className="p-4 text-sm font-semibold text-slate-600">الكيان المرتبط</th>
              <th className="p-4 text-sm font-semibold text-slate-600">الحالة</th>
              <th className="p-4 text-sm font-semibold text-slate-600">تاريخ الإضافة</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {jobs.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-slate-400">لا توجد مهام حالياً في طابور المعالجة.</td>
              </tr>
            ) : (
              jobs.map((job) => (
                <tr key={job.uuid} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 text-sm font-mono text-slate-500">...{job.uuid.slice(-8)}</td>
                  <td className="p-4 text-sm font-medium text-slate-800">{getTaskTypeName(job.task_type)}</td>
                  <td className="p-4 text-sm text-slate-600 capitalize">{job.target_type}</td>
                  <td className="p-4">{getStatusBadge(job.status)}</td>
                  <td className="p-4 text-sm text-slate-500" dir="ltr">
                    {new Date(job.created_at).toLocaleString('en-GB')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}